import type { CalculationPath } from '../types';

export type ParsedElement = { type: 'symbol' | 'number' | 'path' | 'empty'; name?: string };

export const parseLastElement = (formulaInput: string, paths?: CalculationPath[]): ParsedElement => {
  const trimmed = formulaInput.trim();
  if (!trimmed) return { type: 'empty' };

  const pathNames = paths?.map((p) => p.pathName?.trim()).filter(Boolean) || [];
  let i = trimmed.length - 1;

  while (i >= 0 && trimmed[i] === ' ') {
    i--;
  }

  if (i < 0) return { type: 'empty' };

  if (trimmed[i] === ')') {
    return { type: 'symbol', name: ')' };
  }

  if (trimmed[i] === '%') {
    return { type: 'symbol', name: '%' };
  }

  if (i >= 1 && trimmed.substring(i - 1, i + 1) === 'π²') {
    return { type: 'symbol', name: 'π²' };
  }

  if (trimmed[i] === '²' && i >= 1 && trimmed[i - 1] === 'π') {
    return { type: 'symbol', name: 'π²' };
  }

  if (i >= 1 && trimmed.substring(i - 1, i + 1) === 'X²') {
    return { type: 'symbol', name: 'X²' };
  }

  if (trimmed[i] === '²' && i >= 1 && trimmed[i - 1] === 'X') {
    return { type: 'symbol', name: 'X²' };
  }

  if (trimmed[i] === 'π') {
    return { type: 'symbol', name: 'π' };
  }

  if (trimmed[i] === '√') {
    return { type: 'symbol', name: '√' };
  }

  let end = i + 1;
  let start = i;

  while (start >= 0) {
    const char = trimmed[start];
    if (char === undefined || char === ' ') {
      break;
    }
    const operators = ['×', '÷', '&', '(', ')', '%', '√', 'π'];
    if (operators.includes(char)) {
      break;
    }
    if (char === '²' && start > 0 && (trimmed[start - 1] === 'X' || trimmed[start - 1] === 'π')) {
      start--;
      break;
    }
    if (char === '+' || char === '-') {
      if (start > 0) {
        const prevChar = trimmed[start - 1];
        if (prevChar && /[0-9.]/.test(prevChar)) {
          break;
        }
        if (prevChar && prevChar !== ' ' && !/[0-9.()]/.test(prevChar)) {
          break;
        }
      } else {
        break;
      }
    }
    start--;
  }
  start++;

  const candidate = trimmed.substring(start, end).trim();
  if (!candidate) {
    if (start > 0 && trimmed[start - 1] === ')') return { type: 'symbol', name: ')' };
    return { type: 'empty' };
  }

  if (candidate.endsWith('²') && /^[+-]?\d+\.?\d*²$/.test(candidate)) {
    return { type: 'symbol', name: candidate };
  }

  for (const pathName of pathNames) {
    if (pathName && typeof pathName === 'string') {
      const escapedPathName = pathName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const pathRegex = new RegExp(`(^|\\s|[+&×÷(])${escapedPathName}(\\s|[+&×÷)]|$)`);
      const beforeCandidate = trimmed.substring(Math.max(0, start - pathName.length - 2), end + 2);
      if (pathRegex.test(beforeCandidate) && candidate.includes(pathName)) {
        return { type: 'path', name: pathName };
      }
    }
  }

  if (/^[+-]?\d+\.?\d*$/.test(candidate)) {
    return { type: 'number', name: candidate };
  }

  return { type: 'empty' };
};

export const calculateBracketCounter = (formulaInput: string): number => {
  const trimmed = formulaInput.trim();
  let count = 0;
  for (let i = 0; i < trimmed.length; i++) {
    if (trimmed[i] === '(') count++;
    else if (trimmed[i] === ')') count--;
  }
  return count;
};

export const checkFormulaHasPercentage = (formulaInput: string, paths?: CalculationPath[]): boolean => {
  const lastElement = parseLastElement(formulaInput, paths);
  return lastElement.name === '%';
};

export const checkFormulaHasRoot = (formulaInput: string, paths?: CalculationPath[]): boolean => {
  const lastElement = parseLastElement(formulaInput, paths);
  return lastElement.name === '√';
};

export const checkSqAtLast = (formulaInput: string, paths?: CalculationPath[]): boolean => {
  const lastElement = parseLastElement(formulaInput, paths);
  return lastElement.name === 'X²';
};

export const checkPiAtLast = (formulaInput: string, paths?: CalculationPath[]): boolean => {
  const lastElement = parseLastElement(formulaInput, paths);
  return lastElement.name === 'π';
};

export const checkPiSquareAtLast = (formulaInput: string, paths?: CalculationPath[]): boolean => {
  const lastElement = parseLastElement(formulaInput, paths);
  return lastElement.name === 'π²';
};

export const checkNumberSquareAtLast = (formulaInput: string, paths?: CalculationPath[]): boolean => {
  const lastElement = parseLastElement(formulaInput, paths);
  return lastElement.name !== undefined && /^[+-]?\d+\.?\d*²$/.test(lastElement.name);
};

export const checkDisableNumbers = (formulaInput: string, paths?: CalculationPath[]): boolean => {
  const lastElement = parseLastElement(formulaInput, paths);
  return ['X²', 'π', 'π²', ')'].includes(lastElement.name || '') || (lastElement.name !== undefined && /^[+-]?\d+\.?\d*²$/.test(lastElement.name));
};

export const checkDisableSpecials = (formulaInput: string, paths?: CalculationPath[]): boolean => {
  const lastElement = parseLastElement(formulaInput, paths);
  return lastElement.type === 'number';
};

export const hasStringTypePath = (formulaInput: string, paths?: CalculationPath[], checkPathHasStringType?: (path: CalculationPath) => boolean): boolean => {
  const formula = formulaInput.trim();
  if (!formula || !checkPathHasStringType) return false;

  const stringPaths = paths?.filter((path) => path.breadcrumb && path.breadcrumb.trim().length > 0 && checkPathHasStringType(path)) ?? [];
  if (stringPaths.length === 0) return false;

  return stringPaths.some((path) => {
    const pathName = path.pathName?.trim();
    return pathName && formula.includes(pathName);
  });
};

export const hasPathInFormula = (formulaInput: string, paths?: CalculationPath[]): boolean => {
  const formula = formulaInput.trim();
  if (!formula) return false;

  const pathNames = paths?.map((p) => p.pathName?.trim()).filter(Boolean) || [];
  return pathNames.some((pathName) => pathName && formula.includes(pathName));
};

export type FormulaValidationResult = {
  isValid: boolean;
  errorMessage?: string;
};

export const validateFormula = (formulaInput: string, paths?: CalculationPath[]): FormulaValidationResult => {
  const formula = formulaInput.trim();

  if (!formula || formula.length === 0) {
    return {
      isValid: false,
      errorMessage: 'CUSTOM_LIBRARY.CALCULATION_TYPE.FORMULA_MISSING',
    };
  }

  const bracketCounter = calculateBracketCounter(formula);
  if (bracketCounter !== 0) {
    return {
      isValid: false,
      errorMessage: 'CUSTOM_LIBRARY.CALCULATION_TYPE.INVALID_FORMULA',
    };
  }

  const lastElement = parseLastElement(formula, paths);

  if (lastElement.type === 'empty') {
    const trimmedFormula = formula.trim();
    if (
      trimmedFormula.endsWith('+') ||
      trimmedFormula.endsWith('-') ||
      trimmedFormula.endsWith('×') ||
      trimmedFormula.endsWith('÷') ||
      trimmedFormula.endsWith('&')
    ) {
      return {
        isValid: false,
        errorMessage: 'CUSTOM_LIBRARY.CALCULATION_TYPE.INVALID_FORMULA',
      };
    }
    return {
      isValid: false,
      errorMessage: 'CUSTOM_LIBRARY.CALCULATION_TYPE.INVALID_FORMULA',
    };
  }

  if (lastElement.name === '√') {
    return {
      isValid: false,
      errorMessage: 'CUSTOM_LIBRARY.CALCULATION_TYPE.ROOT_AT_LAST',
    };
  }

  if (lastElement.type === 'number' || lastElement.type === 'path') {
    return { isValid: true };
  }

  if (lastElement.type === 'symbol' && lastElement.name) {
    if (formula.trim() === '²' && lastElement.name === '²') {
      return {
        isValid: false,
        errorMessage: 'CUSTOM_LIBRARY.CALCULATION_TYPE.INVALID_FORMULA',
      };
    }
    const validEndingSymbols = [')', '%', 'π', 'π²', 'X²'];
    const isValidNumberSquare = /^[+-]?\d+\.?\d*²$/.test(lastElement.name);
    if (!validEndingSymbols.includes(lastElement.name) && !isValidNumberSquare) {
      return {
        isValid: false,
        errorMessage: 'CUSTOM_LIBRARY.CALCULATION_TYPE.INVALID_FORMULA',
      };
    }
  }

  return { isValid: true };
};
